import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, CircularProgress, Box, Typography, useTheme, IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { StyledButton } from './component/StyledComponent';
import { emailRegex } from './regex/regex';
import { useCommon } from './context/CommonContext';
import { patch, post } from './services/api-services';

const Login = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageObj, setImageObj] = useState(null);
    const { setSnackbar } = useCommon();
    const [passErr, setPassErr] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [staff, setStaff] = useState(false);
    const [staffData, setStaffData] = useState({
        gender: '',
        bio: '',
        name: '',
        userId: '',
        accessToken: ''
    });

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            navigate('/admin/dashboard');
        }
    }, [navigate]);



    const handleLogin = async () => {
        // Validate — show errors via toast
        if (!email && !password) {
            setSnackbar({ openSnackbar: true, message: 'Please enter email and password', severity: 'error' });
            return;
        }
        if (!email) {
            setSnackbar({ openSnackbar: true, message: 'Email is required', severity: 'error' });
            return;
        }
        if (!emailRegex.test(email)) {
            setSnackbar({ openSnackbar: true, message: 'Please enter a valid email address', severity: 'error' });
            return;
        }
        if (!password) {
            setSnackbar({ openSnackbar: true, message: 'Password is required', severity: 'error' });
            return;
        }

        try {
            setLoading(true);
            const credentials = { email: email.toLowerCase(), password };
            const response = await post('/Customers/login?include=["user"]', credentials);

            if (response?.statusCode === 200) {
                const { role, name, id } = response?.data?.user;
                if (role === 'admin' || role === 'staff') {
                    localStorage.setItem('accessToken', response?.data.id);
                    localStorage.setItem('userId', id);
                    localStorage.setItem('userName', name);
                    localStorage.setItem('role', role);

                    if (role === 'admin') {
                        navigate('/admin/dashboard');
                    } else {
                        setStaff(true);
                        setStaffData({ ...staffData, name, userId: id, accessToken: response?.data.id });
                    }
                } else {
                    setSnackbar({ openSnackbar: true, message: 'Unauthorized login attempt', severity: 'error' });
                }
            } else {
                setSnackbar({ openSnackbar: true, message: 'Invalid email or password', severity: 'error' });
            }
        } catch (error) {
            console.error(error);
            setSnackbar({ openSnackbar: true, message: error?.response?.data?.message || 'An error occurred', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };


    const changeListener = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            // setProfilePicUrl(reader.result);
            setImageObj(file);
        }
    };

    const saveStaffData = async () => {
        if (!imageObj) {
            setSnackbar({
                openSnackbar: true,
                message: 'Please select a profile picture',
                severity: 'error'
            })
            return;
        }
        const formData = new FormData();
        formData.append('file', imageObj);
        try {
            setLoading(true);
            const fileUploadResponse = await post(`/Uploads/staff-pic/upload?access_token=${staffData.accessToken}`, formData);
            const image = fileUploadResponse?.data;
            if (!image || !image.result?.files?.file?.[0]?.name) {
                setSnackbar({
                    openSnackbar: true,
                    message: 'File upload failed',
                    severity: 'error'
                })
                return;
            }

            const postData = {
                ...staffData,
                image: image.result.files.file[0].name,
            };

            const patchResponse = await patch(`/Staffs/${staffData.userId}?access_token=${staffData.accessToken}`, postData);

            if (patchResponse?.data) {
                localStorage.setItem('accessToken', staffData.accessToken);
                localStorage.setItem('userId', staffData.userId);
                localStorage.setItem('userName', staffData.name);
                localStorage.setItem('role', 'staff');

                navigate('/admin/dashboard');
            }
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                setSnackbar({
                    openSnackbar: true,
                    message: 'Login expired, please log in again',
                    severity: 'error'
                })
                localStorage.removeItem('accessToken');
                localStorage.removeItem('userId');
                localStorage.removeItem('userName');

                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a1a1a' : '#eff1f5',
                minHeight: '100vh'
            }}
        >
            <Box sx={{ maxWidth: 400, margin: 'auto' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        p: 0,
                        backgroundColor: 'transparent'
                    }}
                >
                    <img
                        src={isDarkMode ? '/whitelogo.png' : '/blacklogo.png'}
                        alt='logo'
                        className='w-[180px]'
                    />
                </Box>
                <Card
                    sx={{
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 40px rgba(0, 0, 0, 0.5)' : '0 3px 1px -2px rgba(0, 0, 0, .2), 0 2px 2px 0 rgba(0, 0, 0, .14), 0 1px 5px 0 rgba(0, 0, 0, .12)',
                        borderRadius: '4px',
                        marginX: { xs: 5, md: 0 },
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#252525' : theme.palette.background.paper,
                        color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                        backgroundImage: 'none'
                    }}
                >
                    <CardContent>
                        <TextField
                            label="Email"
                            variant="standard"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                            margin="normal"
                            autoComplete="email"
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: (theme) => theme.palette.text.primary
                                }
                            }}
                        />
                        <TextField
                            label="Password"
                            variant="standard"
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                            margin="normal"
                            autoComplete="current-password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            size="small"
                                            tabIndex={-1}
                                            sx={{ color: (theme) => theme.palette.text.secondary }}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: (theme) => theme.palette.text.primary
                                }
                            }}
                        />
                        <div className="flex justify-end" style={{ marginTop: '16px' }}>
                            <StyledButton
                                type="button"
                                onClick={handleLogin}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'LOGIN'}
                            </StyledButton>
                        </div>
                    </CardContent>
                </Card>
                <div className="flex justify-center mt-2">
                    <Typography
                        variant="caption"
                        sx={{
                            color: (theme) => theme.palette.text.secondary,
                            fontSize: '0.875rem'
                        }}
                    >
                        Handcrafted by{' '}
                        <a
                            href="https://eulogik.netlify.app/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: isDarkMode ? '#90caf9' : '#3c50b4',
                                textDecoration: 'underline'
                            }}
                        >
                            Eulogik
                        </a>
                    </Typography>
                </div>
                {staff && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: (theme) => theme.palette.background.paper, borderRadius: '8px' }}>
                        <Typography variant="h6" gutterBottom>Staff Info</Typography>
                        <TextField
                            label="Gender"
                            variant="outlined"
                            fullWidth
                            value={staffData.gender || ''}
                            onChange={(e) => setStaffData({ ...staffData, gender: e.target.value })}
                            margin="normal"
                        />
                        <TextField
                            label="Bio"
                            variant="outlined"
                            fullWidth
                            value={staffData.bio || ''}
                            onChange={(e) => setStaffData({ ...staffData, bio: e.target.value })}
                            margin="normal"
                        />
                        <Box sx={{ mt: 2 }}>
                            <input type="file" onChange={changeListener} />
                        </Box>
                        <Button variant="contained" color="primary" fullWidth onClick={saveStaffData} sx={{ marginTop: 2 }}>
                            Save Staff Data
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Login;
